import { BoardInfo, IBoardInfo, IBoardInfoData } from "@/domain/entities/board";
import { ParseError } from "@/repositories/shared/errors/parseError";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";
import { HttpBoardInfo, IBoardInfoParser } from "../boardRepository.types";
import { WeekdayCode, WeekdayCodeName } from "@/types";
import { IEarlyDriveData } from "@/domain/entities/lineInfo";
import { HttpEarlyDriveInfo } from "@/repositories/lineInfo/lineInfoRepository.types";
import dayjs from "dayjs";

function convertWeekdayName(weekdayCode: string): WeekdayCodeName {
  const weekdayCodeMap: Record<string, WeekdayCodeName> = {
    [WeekdayCode.MON]: WeekdayCodeName.MON,
    [WeekdayCode.TUE]: WeekdayCodeName.TUE,
    [WeekdayCode.WED]: WeekdayCodeName.WED,
    [WeekdayCode.THU]: WeekdayCodeName.THU,
    [WeekdayCode.FRI]: WeekdayCodeName.FRI,
    [WeekdayCode.SAT]: WeekdayCodeName.SAT,
    [WeekdayCode.SUN]: WeekdayCodeName.SUN,
    [WeekdayCode.HOLI]: WeekdayCodeName.HOLI,
  };

  return weekdayCodeMap[weekdayCode] ?? WeekdayCodeName.NONE;
}

function sortByWeekday(earlyDriveInfos: IEarlyDriveData[]): IEarlyDriveData[] {
  // 요일에 따라 정렬(월,화,수,목,금,토,일,휴 순)
  const sortedWeekDay = Object.values(WeekdayCode);

  const sortedEarlyDriveInfos = earlyDriveInfos?.sort((a, b) => {
    const indexOfA = sortedWeekDay.findIndex(
      (weekday) => weekday === a.weekdayCode
    );
    const indexOfB = sortedWeekDay.findIndex(
      (weekday) => weekday === b.weekdayCode
    );
    return indexOfA - indexOfB;
  });

  return sortedEarlyDriveInfos;
}

function getEarlyDriveInfos(
  earlyDriveInfos: HttpEarlyDriveInfo[] | undefined
): IEarlyDriveData[] | undefined {
  if (!earlyDriveInfos) return;

  const parsedEarlyDriveInfos = earlyDriveInfos?.map((earlyDriveInfo) => {
    return {
      weekdayCode: earlyDriveInfo.weekdayCode,
      weekdayName: convertWeekdayName(earlyDriveInfo.weekdayCode),
      earlyMinute: earlyDriveInfo.earlyMinute,
    };
  });

  const sortedEarlyDriveInfos = sortByWeekday(parsedEarlyDriveInfos);

  return sortedEarlyDriveInfos;
}

function parseBoardInfo(httpBoardInfo: HttpBoardInfo): IBoardInfo {
  const boardInfoData: IBoardInfoData = {
    id: httpBoardInfo.boardUid,
    lineTypeCode: httpBoardInfo.lineTypeCode,
    lineTypeName: httpBoardInfo.lineTypeName,
    lineRouteNo: httpBoardInfo.lineRouteNo,
    lineRouteName: httpBoardInfo.lineRouteName,
    lineTurnUid: httpBoardInfo.lineTurnUid,
    earlyDriveInfos: getEarlyDriveInfos(httpBoardInfo.earlyDriveWeekly) ?? [],
    carNo: httpBoardInfo.carNo,
    vendorName: httpBoardInfo.vendorName,
    gateNo: httpBoardInfo.gateNo ?? [],
    realGetOnBusStopUid: httpBoardInfo.realOnStationUid,
    realGetOnBusStopName: httpBoardInfo.realOnStationName,
    realGetOnBusStopLat: httpBoardInfo.realOnStationLat,
    realGetOnBusStopLng: httpBoardInfo.realOnStationLng,
    realGetOnDateTime: httpBoardInfo.realOnDtm,
    isEvaluable: !httpBoardInfo.lineSurveyYn, // lineSurveyYn - True : 설문 응답을 했다, False : 설문 응답을 한적이 없다
    allocUid: httpBoardInfo.allocUid,
    campusName: httpBoardInfo.campusName,
    firstStopTime: dayjs(httpBoardInfo.expectedBeginDtm).format("HH:mm"),
    bookDateTime: httpBoardInfo.bookTs
      ? dayjs(httpBoardInfo.bookTs).format("YYYY-MM-DD HH:mm:ss")
      : undefined,
    expectedGetOnBusStopUid: httpBoardInfo.expectedOnStationUid,
    expectedGetOnBusStopName: httpBoardInfo.expectedOnStationName,
    expectedGetOnTime: dayjs(httpBoardInfo.expectedOnDtm).format("HH:mm"),
    getOnBusStopLat: httpBoardInfo.onStationLat,
    getOnBusStopLng: httpBoardInfo.onStationLng,
    expectedGetOffBusStopUid: httpBoardInfo.expectedOffStationUid,
    expectedGetOffBusStopName: httpBoardInfo.expectedOffStationName,
    expectedGetOffTime: dayjs(httpBoardInfo.expectedOffDtm).format("HH:mm"),
    getOffBusStopLat: httpBoardInfo.offStationLat,
    getOffBusStopLng: httpBoardInfo.offStationLng,
  };

  return new BoardInfo(boardInfoData);
}

@injectable()
export class boardInfoParser implements IBoardInfoParser {
  constructor() {}

  toDomain(data: HttpBoardInfo[]): Result<IBoardInfo[], ParseError> {
    try {
      const parsedBoardInfo = data.map((httpBoardInfo) => {
        return parseBoardInfo(httpBoardInfo);
      });
      return ok(parsedBoardInfo);
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }
}
